import { Component, OnInit, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { StepperSelectionEvent } from "@angular/cdk/stepper";
import { MatStepper } from "@angular/material/stepper";
import { TranslocoService } from "@ngneat/transloco";
import { ToastrService } from "ngx-toastr";
import { Compbaser } from "../../../components/compbaser";
import { StoreSelectorsService } from "../../../services";
import { UserService } from "../../../services/user.service";
import { TOAST_FASTER } from "../../../common";

interface IFieldMapping {
    name: string;
    description: string;
    nameError?: string;
}

/**
 * JSON Player Wizard - Multi-step wizard for creating player configurations from JSON
 */
@Component({
    selector: "app-json-player-wizard",
    templateUrl: "json-player-wizard.component.html",
    styleUrls: ["json-player-wizard.component.scss"],
})
export class JsonPlayerWizardComponent extends Compbaser implements OnInit {
    @ViewChild('stepper') stepper: MatStepper;

    propertyFormGroup: FormGroup;
    selectedStep = 0;
    isEditable = true;

    // Step 1: Feed data from dialog-any-feed
    feedData: any = null;

    // Step 2: Field mapping
    fields: IFieldMapping[] = [
        { name: 'text1', description: 'title of the item' },
        { name: 'text2', description: 'description of the item' }
    ];

    // Step 3: Preview data
    isLoadingPreview = false;
    previewError = '';
    previewData: any[] = [];

    constructor(
        public dialogRef: MatDialogRef<JsonPlayerWizardComponent>,
        public override dialog: MatDialog,
        private _formBuilder: FormBuilder,
        private storeSelector: StoreSelectorsService,
        private userService: UserService,
        private toastr: ToastrService,
        private translocoService: TranslocoService,
    ) {
        super();

        this.propertyFormGroup = this._formBuilder.group({
            playerName: ["", Validators.required],
        });
    }

    ngOnInit() {
        // Initialization logic
    }

    onStepChange(e: StepperSelectionEvent) {
        this.selectedStep = e.selectedIndex;

        // When entering step 3 (preview), load the preview data
        if (e.selectedIndex === 2) {
            this.loadPreview();
        }
    }

    /**
     * Add a new field to the mapping
     */
    addField() {
        this.fields.push({
            name: '',
            description: ''
        });
    }

    /**
     * Remove a field from the mapping
     */
    removeField(index: number) {
        if (this.fields.length > 1) {
            this.fields.splice(index, 1);
        }
    }

    /**
     * Validate field name (single word, no spaces/special chars)
     */
    onFieldNameChange(index: number, event: any) {
        const value = event.target.value;
        const field = this.fields[index];

        // Check for invalid characters (spaces, special chars)
        const validPattern = /^[a-zA-Z0-9_]*$/;

        if (!validPattern.test(value)) {
            field.nameError = this.translocoService.translate('ai.fieldNameError', {}, this.translocoService.getActiveLang());
            // Remove invalid characters
            field.name = value.replace(/[^a-zA-Z0-9_]/g, '');
        } else {
            field.nameError = '';
        }

        // Check for duplicate field names
        const duplicates = this.fields.filter(f => f.name && f.name === field.name);
        if (duplicates.length > 1 && field.name) {
            field.nameError = this.translocoService.translate('ai.duplicateFieldName', {}, this.translocoService.getActiveLang());
        }
    }

    /**
     * Load preview data from API
     */
    loadPreview() {
        if (!this.feedData || !this.feedData.prompt) {
            this.previewError = this.translocoService.translate('ai.noPromptError', {}, this.translocoService.getActiveLang());
            return;
        }

        // Validate that all fields have names
        const invalidFields = this.fields.filter(f => !f.name || f.name.trim() === '' || f.nameError);
        if (invalidFields.length > 0) {
            this.previewError = this.translocoService.translate('ai.invalidFieldsError', {}, this.translocoService.getActiveLang());
            // Go back to step 2
            setTimeout(() => {
                if (this.stepper) {
                    this.stepper.selectedIndex = 1;
                }
            }, 100);
            return;
        }

        this.isLoadingPreview = true;
        this.previewError = '';
        this.previewData = [];

        // Build fields object for API
        const fieldsObject: any = {};
        this.fields.forEach(field => {
            fieldsObject[field.name] = field.description;
        });

        // Call feedMeAny API
        this.userService.feedMeAny(
            this.feedData.prompt,
            this.feedData.language || 'en',
            fieldsObject
        ).subscribe({
            next: (response) => {
                this.isLoadingPreview = false;
                if (response && response.success && response.items) {
                    this.previewData = response.items;
                    this.toastr.success(
                        this.translocoService.translate('ai.previewLoaded', {}, this.translocoService.getActiveLang()),
                        this.translocoService.translate('general.success', {}, this.translocoService.getActiveLang()),
                        TOAST_FASTER
                    );
                } else {
                    this.previewError = this.translocoService.translate('ai.invalidResponse', {}, this.translocoService.getActiveLang());
                }
            },
            error: (error) => {
                this.isLoadingPreview = false;
                this.previewError = error.message || this.translocoService.translate('ai.previewError', {}, this.translocoService.getActiveLang());
                this.toastr.error(this.previewError, 'Error', TOAST_FASTER);
            }
        });
    }

    /**
     * Handle feed created event from dialog-any-feed in Step 1
     */
    onFeedCreated(feedData: any) {
        console.log('Feed created in Step 1:', feedData);
        this.feedData = feedData;

        // Move to next step in wizard
        setTimeout(() => {
            if (this.stepper) {
                this.stepper.next();
            }
        }, 100);
    }

    /**
     * Handle cancel event from dialog-any-feed in Step 1
     */
    onFeedCancelled() {
        console.log('Feed creation cancelled in Step 1');
        // Could close the wizard or just stay on step 1
    }

    onCreate() {
        // Create logic
        console.log('Creating JSON player with all wizard data');
        this.dialogRef.close({
            feedData: this.feedData,
            fields: this.fields,
            previewData: this.previewData,
            playerName: this.propertyFormGroup.get('playerName')?.value
        });
    }

    onClose() {
        this.dialogRef.close();
    }
}
